
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useMemo } from "react";

interface Review {
  id: string;
  rating: number;
  review_text: string | null;
  created_at: string;
  product_id: string;
  user_id: string;
  reviewer_metadata: {
    first_name: string;
    last_name: string;
  } | null;
  profiles: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    username: string | null;
  } | null;
}

// List of realistic UK reviewer names
const ukReviewerNames = [
  { first: "Oliver", last: "Smith" },
  { first: "Emma", last: "Jones" },
  { first: "Harry", last: "Williams" },
  { first: "Sophie", last: "Taylor" },
  { first: "James", last: "Brown" },
  { first: "Charlotte", last: "Davies" },
  { first: "George", last: "Evans" },
  { first: "Emily", last: "Wilson" },
  { first: "William", last: "Thomas" },
  { first: "Amelia", last: "Roberts" },
  { first: "Jack", last: "Johnson" },
  { first: "Lily", last: "Lewis" },
  { first: "Thomas", last: "Walker" },
  { first: "Isabella", last: "Wood" },
  { first: "Jacob", last: "Green" },
  { first: "Mia", last: "Hughes" },
  { first: "Ethan", last: "Edwards" },
  { first: "Ava", last: "Clarke" },
  { first: "Daniel", last: "Parker" },
  { first: "Grace", last: "Morris" },
  { first: "Noah", last: "King" },
  { first: "Chloe", last: "Wright" },
  { first: "Lucas", last: "Hill" },
  { first: "Ella", last: "Scott" },
  { first: "Mason", last: "Adams" },
  { first: "Scarlett", last: "Baker" },
  { first: "Logan", last: "Carter" },
  { first: "Aria", last: "Mitchell" },
  { first: "Alexander", last: "Perez" },
  { first: "Luna", last: "Roberts" }
];

// Generate a deterministic number from a string
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

// Helper functions moved outside component for performance
const processReviewerNames = (review: any): any => {
  const needsNameReplacement = 
    !review.profiles?.first_name || !review.profiles?.last_name ||
    (review.reviewer_metadata && (!review.reviewer_metadata.first_name || !review.reviewer_metadata.last_name));
    
  if (!needsNameReplacement) {
    // If reviewer_metadata exists, use that data for consistency
    if (review.reviewer_metadata) {
      return {
        ...review,
        profiles: {
          ...review.profiles,
          first_name: review.reviewer_metadata.first_name,
          last_name: review.reviewer_metadata.last_name
        }
      };
    }
    
    // If no changes needed, return review as is
    return review;
  }
  
  // Use the individual review ID to ensure each review gets a unique name
  // This ensures different reviews get different names even for the same product
  const seedValue = review.id; // Use review ID instead of user_id for uniqueness
  const nameIndex = hashString(seedValue) % ukReviewerNames.length;
  const randomName = ukReviewerNames[nameIndex];
  
  // Apply the replacement consistently to both reviewer_metadata and profiles
  return {
    ...review,
    reviewer_metadata: {
      ...(review.reviewer_metadata || {}),
      first_name: randomName.first,
      last_name: randomName.last
    },
    profiles: {
      ...(review.profiles || {}),
      first_name: randomName.first,
      last_name: randomName.last
    }
  };
};

const isInvalidReviewer = (review: any): boolean => {
  // First, exclude the pawto toy reviews completely
  const pawtoToyId = "94c1928a-e754-45cd-96d6-a409c6d50157";
  if (review.product_id === pawtoToyId) {
    return true;
  }
  
  // Explicitly filter out any review with "jj" or "jj b" in the name (case insensitive)
  const profileFirstName = review.profiles?.first_name?.toLowerCase();
  const profileLastName = review.profiles?.last_name?.toLowerCase();
  const metadataFirstName = review.reviewer_metadata?.first_name?.toLowerCase();
  const metadataLastName = review.reviewer_metadata?.last_name?.toLowerCase();
  
  // Check if any name matches "jj", "jj b", or similar variations
  return (profileFirstName === "jj" || metadataFirstName === "jj") || 
      (profileLastName === "b" || metadataLastName === "b") ||
      (profileFirstName?.includes("jj") && profileLastName?.includes("b")) ||
      (metadataFirstName?.includes("jj") && metadataLastName?.includes("b"));
};

export const useProductReviews = (productIds: string[]) => {
  const queryClient = useQueryClient();
  const productIdsKey = productIds.join(',');

  const { data: reviews, isLoading } = useQuery({
    queryKey: ['product-reviews', productIds],
    queryFn: async () => {
      if (!productIds.length) return {};
      
      const { data, error } = await supabase
        .from('product_reviews')
        .select(`
          id,
          rating,
          review_text,
          created_at,
          product_id,
          user_id,
          reviewer_metadata,
          profiles (
            id,
            first_name,
            last_name,
            username
          )
        `)
        .in('product_id', productIds)
        .neq('rating', 3) // Filter out 3-star reviews
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching reviews:', error);
        throw error;
      }
      
      // Process reviews with separated functions to improve performance
      const processedData = (data || [])
        .filter(review => !isInvalidReviewer(review))
        .map(processReviewerNames);
      
      // Group reviews by product_id
      return (processedData || []).reduce((acc: { [key: string]: Review[] }, review: Review) => {
        const productId = review.product_id;
        if (!acc[productId]) {
          acc[productId] = [];
        }
        acc[productId].push(review);
        return acc;
      }, {});
    },
    enabled: productIds.length > 0,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes to reduce database calls
  });

  // Setup real-time subscription for reviews
  useEffect(() => {
    if (!productIds.length) return;
    
    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'product_reviews',
          filter: `product_id=in.(${productIdsKey})`
        },
        () => {
          // Refetch reviews when changes occur
          queryClient.invalidateQueries({ queryKey: ['product-reviews', productIds] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [productIdsKey, queryClient, productIds]);

  // Memoize average ratings calculation to avoid recalculation on each render
  const averageRatings = useMemo(() => {
    const ratings: { [key: string]: number } = {};
    
    if (reviews) {
      Object.entries(reviews).forEach(([productId, productReviews]) => {
        // Type guard to ensure productReviews is an array with length property
        if (!Array.isArray(productReviews) || productReviews.length === 0) {
          return;
        }
        
        // Now TypeScript knows productReviews is an array with at least one element
        const sum = productReviews.reduce((total, review) => total + review.rating, 0);
        ratings[productId] = parseFloat((sum / productReviews.length).toFixed(1));
      });
    }
    
    return ratings;
  }, [reviews]);

  return { 
    reviews: reviews || {}, 
    averageRatings,
    isLoading 
  };
};
